import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import {
    Card, Box, Tabs, Tab, Typography, Grid, CircularProgress, Chip, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// Medicare data arrives already normalized by the AbilityHub API (helpers/bb-fhir.js
// in AHAPI), the same shape the AH App renders; no FHIR parsing happens here.
// Claims about substance use treatment are never included for FieldWorker users.

// Wording required by the Blue Button API terms of service.
const CMS_ATTRIBUTION = 'This product uses the Blue Button APIs but is not endorsed or certified by the Centers for Medicare & Medicaid Services or the U.S. Department of Health and Human Services.';

const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString() : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');
const fmtMoney = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const fmtRange = (start, end) => (start ? (end && end !== start ? `${fmtDate(start)} – ${fmtDate(end)}` : fmtDate(start)) : '—');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function InfoRow({ label, value }) {
    return (
        <Grid container spacing={1} style={{ marginBottom: 6 }}>
            <Grid size={5}>
                <Typography variant="body2" color="textSecondary" style={{ wordBreak: 'break-word' }}>{label}</Typography>
            </Grid>
            <Grid size={7}>
                <Typography variant="body2" style={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
            </Grid>
        </Grid>
    );
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box style={{ padding: 12, overflowY: 'auto', flex: 1 }}>{children}</Box> : null;
}

function CenteredMessage({ icon, title, subtitle }) {
    return (
        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24, gap: 8 }}>
            {icon}
            <Typography variant="subtitle2" color="textSecondary">{title}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
        </Box>
    );
}

// Fields CMS sends as bare codes, labelled with CMS field names (as-is).
function RawRows({ rows }) {
    return rows.map((r, i) => <InfoRow key={`${r.label}-${i}`} label={r.label} value={r.amount !== undefined ? fmtMoney(r.amount) : r.value} />);
}

function Contact({ contact }) {
    if (!contact) return null;
    return (
        <Box style={{ marginTop: 6 }}>
            {contact.name && <Typography variant="body2" style={{ fontWeight: 500 }}>{contact.name}</Typography>}
            {contact.phones.map((p) => <Typography key={p} variant="body2">{p}</Typography>)}
            {contact.website && (
                <Typography variant="body2" component="a" href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer">
                    {contact.website}
                </Typography>
            )}
        </Box>
    );
}

function Unavailable() {
    return <Typography variant="body2" color="textSecondary">This information could not be loaded from Medicare right now.</Typography>;
}

function PatientTab({ patient, unavailable }) {
    if (!patient) return unavailable ? <Unavailable /> : <Typography variant="body2" color="textSecondary">No patient information available.</Typography>;
    const location = [patient.address?.city, [patient.address?.state, patient.address?.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return (
        <>
            <InfoRow label="Name" value={patient.name} />
            <InfoRow label="Date of Birth" value={fmtDate(patient.birthDate)} />
            <InfoRow label="Gender" value={cap(patient.gender)} />
            <InfoRow label="Medicare Number" value={patient.medicareNumber} />
            <InfoRow label="Location" value={location} />
            <InfoRow label="Sex" value={patient.sex} />
            <InfoRow label="Race" value={patient.race} />
            <InfoRow label="Ethnicity" value={patient.ethnicity} />
            <InfoRow label="Preferred Language" value={patient.languages?.join(', ')} />
            {patient.deceasedDate && <InfoRow label="Date of Death" value={fmtDate(patient.deceasedDate)} />}
            <InfoRow label="Last Updated" value={patient.lastUpdated ? fmtDateTime(patient.lastUpdated) : null} />
            {patient.medicareNumbers?.length > 1 && (
                <InfoRow
                    label="Medicare Number History"
                    value={patient.medicareNumbers.map((m) => `${m.value} (${fmtDate(m.startDate)} – ${m.endDate ? fmtDate(m.endDate) : 'Present'})`).join(', ')}
                />
            )}
            {patient.linkedRecords?.length > 0 && (
                <InfoRow label="Merged Medicare Records" value={patient.linkedRecords.map((l) => `${l.type}: ${l.id}`).join(', ')} />
            )}
        </>
    );
}

function CoverageTab({ coverages, unavailable }) {
    if (!coverages?.length) return unavailable ? <Unavailable /> : <Typography variant="body2" color="textSecondary">No coverage information available.</Typography>;
    return (
        <>
            {coverages.map((c) => (
                <Card key={c.id} variant="outlined" style={{ padding: 10, marginBottom: 10, borderRadius: 8 }}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <Typography variant="subtitle2">{c.title}</Typography>
                        <Chip size="small" label={c.status === 'active' ? 'ACTIVE' : 'ENDED'} color={c.status === 'active' ? 'success' : 'default'} />
                    </Box>
                    <InfoRow label="Coverage Period" value={`${fmtDate(c.startDate)} – ${c.endDate ? fmtDate(c.endDate) : 'Present'}`} />
                    <InfoRow label="Plan" value={c.payor} />
                    <InfoRow label="Member ID" value={c.subscriberId} />
                    <InfoRow label="Relationship" value={c.relationship} />
                    {c.details.length > 0 && (
                        <>
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography variant="caption" color="textSecondary">Medicare Details</Typography>
                            <Box style={{ marginTop: 6 }}>
                                {c.details.map((d) => <InfoRow key={d.label} label={d.label} value={d.value} />)}
                            </Box>
                        </>
                    )}
                    {c.contact && (
                        <>
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography variant="caption" color="textSecondary">Contact</Typography>
                            <Contact contact={c.contact} />
                        </>
                    )}
                    {c.otherDetails.length > 0 && (
                        <>
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography variant="caption" color="textSecondary">Additional Medicare Details</Typography>
                            <RawRows rows={c.otherDetails} />
                        </>
                    )}
                </Card>
            ))}
        </>
    );
}

// The digital Medicare card from $generate-insurance-card, in CMS's colours.
function CardTab({ card, fallbackName, unavailable }) {
    if (!card) return unavailable ? <Unavailable /> : <Typography variant="body2" color="textSecondary">No Medicare card available.</Typography>;
    const first = card.cards[0];
    return (
        <>
            {card.cards.map((c) => {
                const fg = c.colors.foreground || '#FFFFFF';
                const label = { color: fg, opacity: 0.85, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginTop: 6 };
                const rx = c.details.filter((d) => d.label.startsWith('Rx '));
                return (
                    <Box key={c.id} style={{ background: c.colors.background || '#092E86', color: fg, borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
                        <Box style={{ background: c.colors.highlight || '#335097', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
                            {c.logoUrl && <img src={c.logoUrl} alt="" style={{ height: 28 }} />}
                            <Typography variant="subtitle2" style={{ color: fg, fontWeight: 800, letterSpacing: 1 }}>MEDICARE HEALTH INSURANCE</Typography>
                        </Box>
                        <Box style={{ padding: '4px 12px 12px' }}>
                            <Typography variant="caption" style={label}>Name</Typography>
                            <Typography variant="body2" style={{ color: fg, fontWeight: 600 }}>{card.name || fallbackName || '—'}</Typography>
                            <Typography variant="caption" style={label}>Medicare Number</Typography>
                            <Typography variant="h6" style={{ color: fg, fontFamily: 'monospace', letterSpacing: 2 }}>{c.memberId || '—'}</Typography>
                            <Grid container>
                                <Grid size={7}>
                                    <Typography variant="caption" style={label}>Entitled To</Typography>
                                    <Typography variant="body2" style={{ color: fg, fontWeight: 600 }}>{c.title}</Typography>
                                </Grid>
                                <Grid size={5}>
                                    <Typography variant="caption" style={label}>Coverage Starts</Typography>
                                    <Typography variant="body2" style={{ color: fg, fontWeight: 600 }}>{fmtDate(c.startDate)}</Typography>
                                </Grid>
                            </Grid>
                            {c.payor && c.payor !== first.contact?.name && (
                                <>
                                    <Typography variant="caption" style={label}>Plan</Typography>
                                    <Typography variant="body2" style={{ color: fg, fontWeight: 600 }}>{c.payor}</Typography>
                                </>
                            )}
                            {rx.length > 0 && (
                                <Grid container>
                                    {rx.map((r) => (
                                        <Grid size={6} key={r.label}>
                                            <Typography variant="caption" style={label}>{r.label}</Typography>
                                            <Typography variant="body2" style={{ color: fg, fontWeight: 600 }}>{r.value}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Box>
                );
            })}
            <Contact contact={first.contact} />
            {first.notice && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8, whiteSpace: 'pre-line' }}>{first.notice}</Typography>
            )}
        </>
    );
}

function ClaimsTab({ claims, summary, unavailable, onSelectClaim }) {
    if (!claims?.length) return unavailable ? <Unavailable /> : <Typography variant="body2" color="textSecondary">No claims available.</Typography>;

    return (
        <>
            <Card variant="outlined" style={{ padding: 10, marginBottom: 10, borderRadius: 8, background: '#F1F8FC' }}>
                {/* Wraps by the card's own width (Grid breakpoints follow the viewport), so long totals never overlap. */}
                <Box style={{ display: 'flex', flexWrap: 'wrap', rowGap: 6 }}>
                    {[
                        ['Claims', summary.count],
                        ['Billed', fmtMoney(summary.billed)],
                        ['Medicare Paid', fmtMoney(summary.medicarePaid)],
                        ['Patient Paid', fmtMoney(summary.patientPaid)],
                    ].map(([label, value]) => (
                        <Box key={label} style={{ flex: '1 1 120px' }}>
                            <Typography variant="caption" color="textSecondary">{label}</Typography>
                            <Typography variant="subtitle2">{value}</Typography>
                        </Box>
                    ))}
                </Box>
                <Box style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {summary.byType.map((t) => (
                        <Chip key={t.type || t.label} size="small" variant="outlined" label={`${t.label}: ${t.count}`} />
                    ))}
                </Box>
                {summary.processingCount > 0 && (
                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6, fontStyle: 'italic' }}>
                        {summary.processingCount} recent claim(s) are still being processed by Medicare and are not included in these totals.
                    </Typography>
                )}
            </Card>

            {claims.map((c) => (
                <Card
                    key={c.id}
                    variant="outlined"
                    onClick={() => onSelectClaim(c.id)}
                    style={{ padding: 10, marginBottom: 8, borderRadius: 8, cursor: 'pointer', borderLeft: `4px solid ${c.isFinal ? '#4CAF50' : '#FFC107'}` }}
                >
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>{c.typeLabel}</Typography>
                                {!c.isFinal && <Chip size="small" color="warning" variant="outlined" label="Processing" />}
                            </Box>
                            <Typography variant="caption" color="textSecondary">{fmtRange(c.startDate, c.endDate)}</Typography>
                            {c.provider?.name && <Typography variant="caption" color="textSecondary" display="block">{c.provider.name}</Typography>}
                            {c.drugCodes.length > 0 && <Typography variant="caption" color="textSecondary" display="block">NDC {c.drugCodes.join(', ')}</Typography>}
                        </Box>
                        <Box style={{ textAlign: 'right' }}>
                            <Typography variant="body2">{fmtMoney(c.billed)}</Typography>
                            {c.medicarePaid !== null && <Typography variant="caption" color="textSecondary">Medicare paid {fmtMoney(c.medicarePaid)}</Typography>}
                        </Box>
                    </Box>
                </Card>
            ))}
        </>
    );
}

function ClaimDetailDialog({ open, onClose, loading, claim }) {
    const [showAdditional, setShowAdditional] = useState(false);
    useEffect(() => { if (!open) setShowAdditional(false); }, [open]);

    const renderBody = () => {
        if (loading) {
            return <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><CircularProgress size={28} /></Box>;
        }
        if (!claim) {
            return <Typography variant="body2" color="textSecondary">Unable to load claim details.</Typography>;
        }
        return (
            <>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{claim.typeLabel}</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>{fmtRange(claim.startDate, claim.endDate)}</Typography>
                {!claim.isFinal && (
                    <Typography variant="caption" display="block" style={{ color: '#8D6E00', fontStyle: 'italic', marginBottom: 8 }}>
                        Medicare is still processing this claim. Amounts and codes may change or be incomplete until it is final.
                    </Typography>
                )}

                <InfoRow label="Paid Under" value={claim.coverage} />
                {claim.paymentDate && <InfoRow label="Payment Date" value={fmtDate(claim.paymentDate)} />}
                {claim.drugCodes.length > 0 && <InfoRow label="NDC" value={claim.drugCodes.join(', ')} />}
                {claim.identifiers.map((id) => <InfoRow key={id.label} label={id.label} value={id.value} />)}
                {claim.relatedClaims.map((r) => <InfoRow key={r.controlNumber} label={`Related Claim${r.relationship ? ` (${r.relationship})` : ''}`} value={r.controlNumber} />)}
                <InfoRow
                    label="Provider"
                    value={claim.provider ? [claim.provider.name, claim.provider.npi && `NPI ${claim.provider.npi}`, ...claim.provider.identifiers.map((id) => `${id.label} ${id.value}`)].filter(Boolean).join(' · ') : null}
                />
                {claim.facts.map((f) => <InfoRow key={f.label} label={f.label} value={f.value} />)}

                {claim.diagnoses.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Diagnosis Codes</Typography>
                        {claim.diagnoses.map((d, i) => (
                            <Box key={i} style={{ marginBottom: 6 }}>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Typography variant="body2">{d.codeSystem}: {d.code}{d.description ? ` — ${d.description}` : ''}</Typography>
                                    {d.principal && <Chip size="small" color="primary" label="P" />}
                                </Box>
                                {(d.kind || d.presentOnAdmission) && (
                                    <Typography variant="caption" color="textSecondary">
                                        {[d.kind, d.presentOnAdmission && `Present on admission: ${d.presentOnAdmission}`].filter(Boolean).join(' · ')}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </>
                )}

                {claim.procedures.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Procedures</Typography>
                        {claim.procedures.map((p, i) => (
                            <InfoRow key={i} label={p.code} value={[p.description, p.date && fmtDate(p.date)].filter(Boolean).join(' · ')} />
                        ))}
                    </>
                )}

                {claim.amounts.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Financial Summary</Typography>
                        {claim.amounts.map((a) => <InfoRow key={a.label} label={a.label} value={fmtMoney(a.amount)} />)}
                    </>
                )}

                {claim.careTeam.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Care Team</Typography>
                        {claim.careTeam.map((m, i) => (
                            <Box key={i} style={{ marginBottom: 8 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>{m.name || `NPI ${m.npi}`}</Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    {[m.role || 'Provider', m.name && m.npi && `NPI ${m.npi}`].filter(Boolean).join(' · ')}
                                </Typography>
                                {m.qualifications.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" display="block" style={{ wordBreak: 'break-word' }}>
                                        {m.qualifications.join(' · ')}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </>
                )}

                {claim.items.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Service Lines</Typography>
                        {claim.items.map((it) => (
                            <Card key={it.sequence} variant="outlined" style={{ padding: 8, marginBottom: 8, borderRadius: 8 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                    {it.description || (it.code ? `${it.codeType} ${it.code}` : `Service ${it.sequence}`)}
                                </Typography>
                                {it.date && <Typography variant="caption" color="textSecondary" display="block">{fmtRange(it.date, it.endDate)}</Typography>}
                                {it.code && it.description && <Typography variant="caption" color="textSecondary" display="block">{it.codeType}: {it.code}</Typography>}
                                {it.revenueCode && <Typography variant="caption" color="textSecondary" display="block">Revenue Code: {it.revenueCode}</Typography>}
                                {it.quantity && <Typography variant="caption" color="textSecondary" display="block">Quantity: {it.quantity}</Typography>}
                                {it.modifiers.length > 0 && <Typography variant="caption" color="textSecondary" display="block">Modifiers: {it.modifiers.join(', ')}</Typography>}
                                {it.placeOfService && <Typography variant="caption" color="textSecondary" display="block">Place of Service: {it.placeOfService}</Typography>}
                                {it.diagnosisCodes.length > 0 && <Typography variant="caption" color="textSecondary" display="block">Diagnoses: {it.diagnosisCodes.join(', ')}</Typography>}
                                {it.ingredients.map((ing, i) => (
                                    <Typography key={i} variant="caption" color="textSecondary" display="block">Ingredient: NDC {ing.code}{ing.quantity ? ` · ${ing.quantity}` : ''}</Typography>
                                ))}
                                {it.amounts.map((a) => <InfoRow key={a.label} label={a.label} value={fmtMoney(a.amount)} />)}
                                {showAdditional && <RawRows rows={[...it.otherDetails, ...it.otherAmounts]} />}
                            </Card>
                        ))}
                    </>
                )}

                {claim.controlNumber && <Typography variant="caption" color="textSecondary" display="block">Claim number: {claim.controlNumber}</Typography>}

                <Button size="small" onClick={() => setShowAdditional(!showAdditional)} style={{ marginTop: 10 }}>
                    {showAdditional ? 'Hide additional Medicare details' : 'Show additional Medicare details'}
                </Button>
                {showAdditional && (
                    <>
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            Shown as Medicare sends them, using CMS field names and codes.
                        </Typography>
                        <RawRows rows={claim.additionalDetails} />
                        {claim.additionalAmounts.length > 0 && (
                            <>
                                <Typography variant="subtitle2" style={{ marginTop: 8 }}>Other Amounts</Typography>
                                <RawRows rows={claim.additionalAmounts} />
                            </>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Claim Details</DialogTitle>
            <DialogContent dividers>{renderBody()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function MedicareSection({ custEmail, userEmail }) {
    const [activeTab, setActiveTab] = useState(0);
    const [state, setState] = useState({ loading: true, hasAccess: false, connected: false, error: null });
    const [data, setData] = useState(null);

    const [claimOpen, setClaimOpen] = useState(false);
    const [claimLoading, setClaimLoading] = useState(false);
    const [claim, setClaim] = useState(null);

    const fetchData = useCallback(async () => {
        if (!custEmail || !userEmail) {
            setState({ loading: false, hasAccess: false, connected: false, error: null });
            return;
        }
        setState((s) => ({ ...s, loading: true }));
        try {
            const res = await axios.get(APIURL + '/ability_hub/medicare', {
                params: { userEmail, custEmail },
            });
            setData(res.data?.data || null);
            setState({ loading: false, hasAccess: !!res.data?.hasAccess, connected: !!res.data?.connected, error: res.data?.error || null });
        } catch (err) {
            const status = err.response?.status;
            if (status === 403) {
                setState({ loading: false, hasAccess: false, connected: false, error: null });
            } else {
                setState({ loading: false, hasAccess: true, connected: false, error: 'Failed to load Medicare data.' });
            }
        }
    }, [custEmail, userEmail]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openClaim = async (claimId) => {
        setClaim(null);
        setClaimOpen(true);
        setClaimLoading(true);
        try {
            const res = await axios.get(APIURL + `/ability_hub/medicare/claim/${encodeURIComponent(claimId)}`, {
                params: { userEmail, custEmail },
            });
            setClaim(res.data?.data || null);
        } catch (err) {
            setClaim(null);
        }
        setClaimLoading(false);
    };

    const header = (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
            <CreditCardIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Medicare Data</Typography>
        </Box>
    );

    const unavailable = data?.unavailable || [];

    const renderContent = () => {
        if (state.loading) {
            return <Box style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress size={26} /></Box>;
        }
        if (!state.hasAccess) {
            return (
                <CenteredMessage
                    icon={<LockOutlinedIcon style={{ fontSize: 36, color: '#9e9e9e' }} />}
                    title="No access to Medicare data"
                    subtitle="You don't have Health permission for this client's AbilityHUB record."
                />
            );
        }
        if (!state.connected) {
            return (
                <CenteredMessage
                    icon={<CreditCardOffIcon style={{ fontSize: 36, color: '#9e9e9e' }} />}
                    title="Medicare not connected"
                    subtitle="This client has not connected Medicare in the AbilityHUB app yet, or their connection has ended."
                />
            );
        }
        if (state.error) {
            return <CenteredMessage icon={<CreditCardOffIcon style={{ fontSize: 36, color: '#9e9e9e' }} />} title={state.error} />;
        }
        return (
            <>
                <Box style={{ paddingLeft: 8, paddingRight: 8, borderBottom: '1px solid #e0e0e0' }}>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
                        <Tab label="PATIENT" value={0} />
                        <Tab label="CARD" value={3} />
                        <Tab label="COVERAGE" value={1} />
                        <Tab label="CLAIMS" value={2} />
                    </Tabs>
                </Box>
                <TabPanel value={activeTab} index={0}><PatientTab patient={data?.patient} unavailable={unavailable.includes('patient')} /></TabPanel>
                <TabPanel value={activeTab} index={3}>
                    <CardTab card={data?.insuranceCard} fallbackName={data?.patient?.name} unavailable={unavailable.includes('insuranceCard')} />
                </TabPanel>
                <TabPanel value={activeTab} index={1}><CoverageTab coverages={data?.coverages} unavailable={unavailable.includes('coverage')} /></TabPanel>
                <TabPanel value={activeTab} index={2}>
                    <ClaimsTab claims={data?.claims} summary={data?.claimsSummary} unavailable={unavailable.includes('claims')} onSelectClaim={openClaim} />
                </TabPanel>
                <Typography variant="caption" color="textSecondary" style={{ padding: '6px 12px', fontSize: 10, lineHeight: 1.3 }}>
                    {CMS_ATTRIBUTION}
                </Typography>
            </>
        );
    };

    return (
        <Card elevation={3} style={{ height: '100%', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {header}
            {renderContent()}
            <ClaimDetailDialog open={claimOpen} onClose={() => setClaimOpen(false)} loading={claimLoading} claim={claim} />
        </Card>
    );
}

export default MedicareSection;
